import { Suspense, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createPortal } from "react-dom";
import { confirmDialog } from "@/common/components/ConfirmDialog";
import { queryKeys } from "@/lib/query/keys";
import { useSession } from "@/features/auth/hooks/useSession";
import { getActiveWorkspaceId } from "@/lib/api/workspace-context";
import {
  getPeopleMap,
  savePerson,
  manageTag,
  type PeopleMap as PeopleMapData,
} from "@/lib/api/endpoints/profile-interview";
import { listObjects } from "@/lib/api/endpoints/objects";
import { lazyWithRetry } from "@/lib/lazy-with-retry";
import { InterviewPanel } from "@/features/interview/InterviewPanel";
import "./PeopleMap.css";

// Lazy so this eager Synthesis chain doesn't pull ContactEditModal (and the
// whole contacts edit stack) into the entry chunk; opens on card click only.
const ContactCardPopover = lazyWithRetry(() =>
  import("@/modules/contacts/components/ContactCardPopover").then((m) => ({ default: m.ContactCardPopover })),
);

// Varied openers so it never reads the same twice.
const TALK_SEEDS = [
  "Tell me about a person in your life — or a way you'd like to group your people. Who's on your mind?",
  "Who should I know about? A friend, family, a colleague — or a group you'd like to organize.",
  "Bring up anyone — and how they fit in your world. Where should we start?",
  "Who's someone important to you, or a grouping you'd like me to track?",
  "Let's add to your people. Who comes to mind, or what group are you thinking about?",
];
const pickSeed = (arr: string[]) => arr[Math.floor(Math.random() * arr.length)];

const MAX_CHIPS = 16;

/** Strip any " — relationship" suffix the model may append. */
function cleanName(member: string): string {
  return member.split(/\s+[—–-]\s+/)[0].trim();
}

interface Contact {
  id: string;
  name: string;
  tags: string[];
  note: string;
  birthday: string;
}
interface EditState {
  id?: string; // undefined => adding
  name: string;
  tags: string[];
  note: string;
  birthday: string;
}

export function PeopleMap() {
  const { token } = useSession();
  const ws = getActiveWorkspaceId();
  const queryClient = useQueryClient();
  const queryKey = queryKeys.synthesis.tab(ws, "people");

  const loadPeopleMap = async (force = false): Promise<{ map: PeopleMapData; byName: Map<string, Contact> }> => {
    const [map, contacts] = await Promise.all([
      getPeopleMap(token ?? null, ws, force),
      listObjects(token ?? null, { type: "contact", limit: 500 }),
    ]);
    const m = new Map<string, Contact>();
    for (const c of (contacts.items ?? []) as any[]) {
      const a = c.attributes ?? c ?? {};
      const name = c.title || a.name || "";
      m.set(name.trim().toLowerCase(), {
        id: c.id,
        name,
        tags: Array.isArray(a.tags) ? a.tags.map((t: unknown) => String(t)) : [],
        note: String(a.note ?? ""),
        birthday: String(a.birthday ?? ""),
      });
    }
    return { map, byName: m };
  };

  const { data: queryData, isLoading: loading } = useQuery({
    queryKey,
    queryFn: () => loadPeopleMap(false),
    enabled: Boolean(token),
    staleTime: 5 * 60 * 1000,
  });
  const data: PeopleMapData | null = queryData?.map ?? null;
  const byName: Map<string, Contact> = queryData?.byName ?? new Map();

  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [edit, setEdit] = useState<EditState | null>(null);
  const [saving, setSaving] = useState(false);
  const [renameTag, setRenameTag] = useState<{ from: string; to: string } | null>(null);
  const [newTag, setNewTag] = useState("");
  const [tagInput, setTagInput] = useState(""); // type-ahead in the person editor
  const [search, setSearch] = useState("");
  const [viewContactId, setViewContactId] = useState<string | null>(null);
  const [manageOpen, setManageOpen] = useState(false);
  const [talkSeed, setTalkSeed] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const bucketsContaining = (name: string): string[] => {
    const lower = name.toLowerCase();
    return (data?.buckets ?? [])
      .filter((b) => b.isPeople && b.members.some((m) => cleanName(m).toLowerCase() === lower))
      .map((b) => b.name);
  };

  const openChip = (name: string) => {
    const c = byName.get(name.toLowerCase());
    setEdit({
      id: c?.id,
      name,
      tags: c?.tags && c.tags.length ? c.tags : bucketsContaining(name),
      note: c?.note ?? "",
      birthday: c?.birthday ?? "",
    });
  };

  const persist = async (input: Parameters<typeof savePerson>[2]) => {
    setSaving(true);
    setSaveError(null);
    try {
      await savePerson(token ?? null, ws, input);
      setEdit(null);
      // Keep the board visible — the refresh is cache-fast (no LLM), so it
      // updates in place instead of flashing the full "Mapping…" spinner.
      await queryClient.invalidateQueries({ queryKey });
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : "Couldn't save — try again.");
    } finally {
      setSaving(false);
    }
  };

  const toggleTag = (tag: string) =>
    setEdit((e) =>
      e ? { ...e, tags: e.tags.includes(tag) ? e.tags.filter((t) => t !== tag) : [...e.tags, tag] } : e,
    );

  const save = () => {
    if (!edit || saving || !edit.name.trim()) return;
    persist({
      id: edit.id,
      name: edit.name.trim(),
      tags: edit.tags,
      note: edit.note,
      birthday: edit.birthday,
    });
  };
  const remove = async () => {
    if (!edit?.id || saving) return;
    if (!(await confirmDialog({ message: `Delete ${edit.name || "this person"} from your contacts entirely? This removes the whole contact, not just a tag.`, confirmLabel: "Delete", danger: true }))) return;
    persist({ id: edit.id, remove: true });
  };

  const doTag = async (body: Parameters<typeof manageTag>[2]) => {
    await manageTag(token ?? null, ws, body);
    await queryClient.invalidateQueries({ queryKey });
  };

  const allTags: string[] = (data?.allTags && data.allTags.length
    ? data.allTags
    : (data?.buckets ?? []).filter((b) => b.isPeople).map((b) => b.name)) ?? [];

  const bucketCount = (name: string) =>
    (data?.buckets ?? []).find((b) => b.name === name)?.members.length ?? 0;

  // Filter members by the search query; drop empty buckets while searching.
  const q = search.trim().toLowerCase();
  const filteredBuckets = (data?.buckets ?? [])
    .map((b) => (q ? { ...b, members: b.members.filter((m) => cleanName(m).toLowerCase().includes(q)) } : b))
    .filter((b) => b.members.length > 0);

  const renderBucket = (b: PeopleMapData["buckets"][number]) => {
    const isOpen = expanded.has(b.name) || q.length > 0; // searching shows all matches
    const shown = isOpen ? b.members : b.members.slice(0, MAX_CHIPS);
    const hidden = b.members.length - shown.length;
    return (
      <div key={b.name} className={`people-bucket${b.isPeople ? "" : " is-service"}`}>
        <div className="people-bucket-head">
          <span className="people-bucket-name">{b.name}</span>
          <span className="people-bucket-count">{b.members.length}</span>
        </div>
        <div className="people-bucket-chips">
          {shown.map((m) => {
            const name = cleanName(m);
            return (
              <button key={m} className="people-chip" onClick={() => openChip(name)} title="Click to edit / re-file">
                {name}
              </button>
            );
          })}
          {hidden > 0 && (
            <button className="people-chip people-chip-more" onClick={() => setExpanded((s) => new Set(s).add(b.name))}>
              +{hidden} more
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="people-map">
      <div className="people-map-bar">
        <button className="people-add-btn people-talk-btn" onClick={() => setTalkSeed(pickSeed(TALK_SEEDS))}>
          💬 Talk to Xavior about people
        </button>
        <button
          className={`people-remap-btn${manageOpen ? " is-open" : ""}`}
          onClick={() => setManageOpen((o) => !o)}
        >
          ⚙ Manage tags
        </button>
        <input
          className="people-search"
          value={search}
          placeholder="🔍 Search people…"
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* Tag management — toggled inline (the groupings below already serve as
          the legend, so there's no always-on tag bar). */}
      {manageOpen && (
        <div className="people-tagbar">
          {allTags.map((t) =>
            renameTag?.from === t ? (
              <span key={t} className="people-tagbar-edit">
                <input
                  autoFocus
                  value={renameTag.to}
                  onChange={(e) => setRenameTag({ from: t, to: e.target.value })}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && renameTag.to.trim() && renameTag.to.trim() !== t) {
                      doTag({ action: "rename", from: t, to: renameTag.to.trim() });
                      setRenameTag(null);
                    } else if (e.key === "Escape") setRenameTag(null);
                  }}
                  onBlur={() => setRenameTag(null)}
                />
              </span>
            ) : (
              <span key={t} className="people-tagbar-tag">
                <button className="people-tagbar-name" onClick={() => setRenameTag({ from: t, to: t })} title="Rename">
                  {t} <span className="people-bucket-count">{bucketCount(t)}</span>
                </button>
                <button
                  className="people-tagbar-del"
                  title="Delete tag"
                  onClick={async () => {
                    if (await confirmDialog({ message: `Delete the “${t}” tag? It's removed from everyone who has it.`, confirmLabel: "Delete", danger: true })) {
                      doTag({ action: "delete", tag: t });
                    }
                  }}
                >
                  ×
                </button>
              </span>
            ),
          )}
          <input
            className="people-tagbar-new"
            value={newTag}
            placeholder="+ new tag"
            onChange={(e) => setNewTag(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && newTag.trim()) {
                doTag({ action: "create", tag: newTag.trim() });
                setNewTag("");
              }
            }}
          />
        </div>
      )}

      {loading ? (
        <div className="people-map-loading">Mapping your people…</div>
      ) : !data || data.buckets.length === 0 ? (
        <div className="people-map-empty">No people mapped yet — add someone or tell Xavior below.</div>
      ) : (
        <>
          {filteredBuckets.length === 0 ? (
            <div className="people-map-empty">No one matches "{search}".</div>
          ) : (
            <>
              <div className="people-map-grid">{filteredBuckets.filter((b) => b.isPeople).map(renderBucket)}</div>
              {filteredBuckets.some((b) => !b.isPeople) && (
                <div className="people-map-services">{filteredBuckets.filter((b) => !b.isPeople).map(renderBucket)}</div>
              )}
            </>
          )}
        </>
      )}

      {edit &&
        createPortal(
          <div className="people-edit-overlay" onMouseDown={() => setEdit(null)}>
            <div className="people-edit" onMouseDown={(e) => e.stopPropagation()}>
              <div className="people-edit-title">{edit.id ? "Edit person" : "Add person"}</div>

              {edit.id && (
                <button
                  className="people-in-contacts"
                  title="Open the full contact card"
                  onClick={() => setViewContactId(edit.id!)}
                >
                  ✓ In your contacts · View full contact ↗
                </button>
              )}

              <label className="people-edit-field">
                <span>Name</span>
                <input
                  value={edit.name}
                  onChange={(e) => setEdit({ ...edit, name: e.target.value })}
                  autoFocus={!edit.id}
                />
              </label>

              <div className="people-edit-field">
                <span>Tags</span>
                <div className="people-tags-selected">
                  {edit.tags.length === 0 && <span className="people-tags-none">No tags yet</span>}
                  {edit.tags.map((t) => (
                    <span key={t} className="people-tag">
                      {t}
                      <button className="people-tag-x" onClick={() => toggleTag(t)} aria-label={`Remove ${t}`}>
                        ×
                      </button>
                    </span>
                  ))}
                </div>
                {/* Type-ahead: autocomplete existing tags, or Enter to create new */}
                <div className="people-typeahead">
                  <input
                    className="people-cat-new"
                    value={tagInput}
                    placeholder="type to add a tag…"
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        const v = tagInput.trim();
                        if (v && !edit.tags.includes(v)) setEdit({ ...edit, tags: [...edit.tags, v] });
                        setTagInput("");
                      }
                    }}
                  />
                  {tagInput.trim() &&
                    (() => {
                      const q = tagInput.trim().toLowerCase();
                      const matches = allTags
                        .filter((t) => !edit.tags.includes(t) && t.toLowerCase().includes(q))
                        .slice(0, 8);
                      const exact = allTags.some((t) => t.toLowerCase() === q);
                      if (matches.length === 0 && exact) return null;
                      return (
                        <div className="people-typeahead-menu">
                          {matches.map((t) => (
                            <button
                              key={t}
                              className="people-typeahead-item"
                              onClick={() => {
                                setEdit({ ...edit, tags: [...edit.tags, t] });
                                setTagInput("");
                              }}
                            >
                              {t}
                            </button>
                          ))}
                          {!exact && (
                            <button
                              className="people-typeahead-item people-typeahead-new"
                              onClick={() => {
                                setEdit({ ...edit, tags: [...edit.tags, tagInput.trim()] });
                                setTagInput("");
                              }}
                            >
                              ＋ Create "{tagInput.trim()}"
                            </button>
                          )}
                        </div>
                      );
                    })()}
                </div>
              </div>

              <label className="people-edit-field">
                <span>Description</span>
                <textarea
                  rows={3}
                  value={edit.note}
                  placeholder={`Enter a few words about ${edit.name.trim().split(/\s+/)[0] || "them"}…`}
                  onChange={(e) => setEdit({ ...edit, note: e.target.value })}
                />
              </label>

              <label className="people-edit-field">
                <span>Birthday</span>
                <input
                  value={edit.birthday}
                  placeholder="when's their birthday?"
                  onChange={(e) => setEdit({ ...edit, birthday: e.target.value })}
                />
              </label>

              {saveError && <div className="people-save-error">⚠ {saveError}</div>}

              <div className="people-edit-actions">
                {edit.id && (
                  <button className="people-edit-remove" onClick={remove} disabled={saving}>
                    Delete contact
                  </button>
                )}
                <button className="people-edit-cancel" onClick={() => setEdit(null)} disabled={saving}>
                  Cancel
                </button>
                <button className="people-edit-save" onClick={save} disabled={saving || !edit.name.trim()}>
                  {saving ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {viewContactId && (
        <Suspense fallback={null}>
          <ContactCardPopover
            objectId={viewContactId}
            objectType="contact"
            onClose={() => setViewContactId(null)}
          />
        </Suspense>
      )}

      {talkSeed && (
        <InterviewPanel
          seedQuestion={talkSeed}
          onClose={() => setTalkSeed(null)}
          onComplete={() => {
            void queryClient.invalidateQueries({ queryKey });
          }}
        />
      )}
    </div>
  );
}

export default PeopleMap;
